import {
  Badge,
  Button,
  CustomInput,
  Link,
  Modal,
  Typography,
} from '@globalmed/atlas-ui';
import { memo, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { FormBuilderSignatureModal } from '../FormBuilderSignatureModal';
import styles from './FormBuilderHeader.module.css';
import { formBuilderHeaderConfig } from './config';
import {
  TFormBuilderPreviewModeSize,
  TFormBuilderEditSchemaCamel,
  TSignatureSettingsCamel,
  TFormBuilderContent,
  ESignatureSettingsRequiredBy,
} from '@/types';

type THeaderPreviewMode = {
  variant: 'preview';
  modeSize: TFormBuilderPreviewModeSize;
  onModeSizeChange: (size: TFormBuilderPreviewModeSize) => void;
};

type THeaderEditMode = { variant: 'edit' };

type TProps = (THeaderPreviewMode | THeaderEditMode) & {
  contentMode?: TFormBuilderContent;
};

export const FormBuilderHeader =
  /**
   * Header component for form builder with title editing, mode switching and navigation.
   * The Publish button submits the surrounding form and shows a loader while it's submitting;
   * repeated submits are ignored by the form's submit guard.
   * @param {TProps} props - Component props
   * @param {string} props.variant - Header variant ('edit' or 'preview')
   * @param {TFormBuilderContent} [props.contentMode='formBuilder'] - Content mode for the form builder
   * @param {TFormBuilderPreviewModeSize} [props.modeSize] - Preview mode size (desktop/mobile), required when variant is 'preview'
   * @param {Function} [props.onModeSizeChange] - Callback when preview mode size changes, required when variant is 'preview'
   * @returns {JSX.Element} Form builder header component
   */
  memo(({ contentMode = 'formBuilder', ...props }: TProps) => {
    const { formBuilderId } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const userType = location.state?.userType;
    const {
      watch,
      setValue,
      formState: { isDirty, isSubmitting },
    } = useFormContext<TFormBuilderEditSchemaCamel>();

    const { showSignature, headerModalTemplate, ...otherContent } =
      formBuilderHeaderConfig[contentMode];

    const { redirect } = otherContent[props.variant];

    const watchTitle = watch('title');
    const settingsSignature = watch('signatureSettings');
    const fields = watch('fields');

    const [openExitModal, setOpenExitModal] = useState(false);
    const [title, setTitle] = useState(watchTitle);
    const [isTitleOpen, setIsTitleOpen] = useState(
      formBuilderId === 'empty' && !watchTitle,
    );
    const [isSignatureSettingsOpen, setIsSignatureSettingsOpen] =
      useState(false);

    useEffect(() => {
      if (!watchTitle) return;
      setTitle(watchTitle);
    }, [watchTitle]);

    const onTitleSave = () => {
      setValue('title', title);
      setIsTitleOpen(false);
    };

    const onSettingsSignatureSave = (values: TSignatureSettingsCamel) => {
      const currentValues: TSignatureSettingsCamel = {
        ...values,
        requiredBy:
          userType === 'provider'
            ? ESignatureSettingsRequiredBy.PROVIDER
            : values.requiredBy,
      };
      setValue('signatureSettings', currentValues);
    };

    const redirectToMode = () =>
      navigate(
        generatePath(redirect.mode, {
          formBuilderId: formBuilderId ?? 'empty',
        }),
        {
          state: location.state,
        },
      );

    const redirectToMain = () => navigate(redirect.main);

    const defaultTitle = watchTitle || t('form-builder.untitled-template');
    const isEdit = props.variant === 'edit';
    const isPreview = props.variant === 'preview';

    const onBackClick = () => {
      if (isPreview || !isDirty) {
        redirectToMain();
        return;
      }
      setOpenExitModal(true);
    };

    return (
      <>
        <div className={styles.container}>
          <div className={styles.templateNameWrapper}>
            <Link
              preIcon="ArrowNarrowLeft"
              color="black"
              aria-label={t('common.back')}
              onClick={onBackClick}
            />
            <div className={styles.nameContent}>
              {isEdit ? (
                <Link
                  text={defaultTitle}
                  color={!watchTitle ? 'grey' : 'black'}
                  weight="semibold"
                  postIcon={watchTitle ? 'Edit' : undefined}
                  onClick={() => setIsTitleOpen(true)}
                  className={styles.nameLink}
                />
              ) : (
                <Typography
                  fontSize="20px"
                  fontWeight={600}
                  color={!watchTitle ? 'grey' : 'black'}
                >
                  {defaultTitle}
                </Typography>
              )}
              {userType && (
                <Badge
                  sx={{ width: 'max-content' }}
                  text={t(userType ?? '')}
                  size="sm"
                  color="white"
                />
              )}
            </div>
          </div>
          {isPreview && (
            <div className={styles.btns}>
              <Link
                preIcon="DisplayMonitor"
                color={
                  props.modeSize === 'desktop'
                    ? 'var(--colors-primary600)'
                    : 'var(--colors-gray950)'
                }
                aria-label={t('form-builder.preview-desktop')}
                aria-pressed={props.modeSize === 'desktop'}
                onClick={() => props.onModeSizeChange('desktop')}
              />
              <Link
                preIcon="DisplayPhone"
                color={
                  props.modeSize === 'mobile'
                    ? 'var(--colors-primary600)'
                    : 'var(--colors-gray950)'
                }
                aria-label={t('form-builder.preview-mobile')}
                aria-pressed={props.modeSize === 'mobile'}
                onClick={() => props.onModeSizeChange('mobile')}
              />
            </div>
          )}
          <div className={styles.btns}>
            {showSignature && (
              <Link
                preIcon="SettingsGear"
                color="black"
                aria-label={t('common.signature-settings')}
                onClick={() => setIsSignatureSettingsOpen(true)}
              />
            )}

            <Button
              variant="secondary"
              text={isEdit ? t('common.preview') : t('edit')}
              preIcon={isEdit ? 'Eye' : 'Edit'}
              onClick={redirectToMode}
            />
            <Button
              text={t('form-builder.publish')}
              isSubmit
              loading={isSubmitting}
              disabled={!fields.length}
            />

            {isEdit && (
              <Link
                preIcon="XClose"
                color="black"
                aria-label={t('close')}
                onClick={() => setOpenExitModal(true)}
              />
            )}
          </div>
        </div>
        {showSignature && (
          <FormBuilderSignatureModal
            open={isSignatureSettingsOpen}
            onClose={() => setIsSignatureSettingsOpen(false)}
            title={t('form-builder.signature.settings-description')}
            defaultType={settingsSignature?.type}
            defaultRequiredBy={settingsSignature?.requiredBy}
            userType={userType}
            contentMode={contentMode}
            onSave={onSettingsSignatureSave}
          />
        )}
        <Modal
          open={isTitleOpen}
          size="small"
          onOpenChange={watchTitle ? setIsTitleOpen : undefined}
          title={t(
            watchTitle ? headerModalTemplate.edit : headerModalTemplate.new,
          )}
          body={
            <div className={styles.modalContainer}>
              <CustomInput
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                sx={{ width: '100%' }}
                aria-required="true"
                labelProps={{
                  label: t('common.template-name'),
                  required: true,
                }}
              />
              <div className={styles.btns}>
                <Button
                  text={t('cancel')}
                  variant="secondary"
                  onClick={() => {
                    setIsTitleOpen(false);
                    redirectToMain();
                  }}
                />
                <Button
                  text={t('common.save')}
                  onClick={onTitleSave}
                  disabled={!title || watchTitle === title}
                />
              </div>
            </div>
          }
        />
        <Modal
          open={openExitModal}
          onOpenChange={setOpenExitModal}
          title={t('common.exit-header')}
          body={
            <div className={styles.exitModalContainer}>
              <Typography
                sx={{
                  color: 'var(--colors-secondary)',
                  fontSize: '14px',
                  fontWeight: 400,
                }}
              >
                {t('form-builder.exit-text')}
              </Typography>
              <div className={styles.btns}>
                <Button
                  text={t('cancel')}
                  variant="secondary"
                  onClick={() => setOpenExitModal(false)}
                />
                <Button
                  text={t('common.exit-button')}
                  color="destructive"
                  onClick={redirectToMain}
                />
              </div>
            </div>
          }
        />
      </>
    );
  });

FormBuilderHeader.displayName = 'FormBuilderHeader';
